import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Calendar, ChevronRight, Info } from 'lucide-react'

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '../../ui/drawer'
import { ruleSetFromRecurrenceRule, levelArrayToString } from '../../../utilities/helpers'
import { cn } from '../../../lib/utils'

function DayEventsDrawer({ open, onClose, date, timeZone, klasses, onEventOpenClass, onEventPreview }) {
  if (!date || !klasses || klasses.length === 0) {
    return null
  }

  const formattedDate = moment(date).tz(timeZone).format('dddd, MMMM D, YYYY')
  const dayKey = moment.tz(date, timeZone).format('YYYY-MM-DD')
  const dayStart = moment.tz(date, timeZone).startOf('day')
  const dayEnd = moment.tz(date, timeZone).endOf('day')

  const allEventsForDay = klasses.flatMap((klass) => {
    const ruleSet = ruleSetFromRecurrenceRule(klass.recurrenceRule)
    const dates = ruleSet.between(dayStart.toDate(), dayEnd.toDate(), true)

    return dates.map((klassDate) => ({
      klassId: klass.id,
      klassDate,
      time: moment(klassDate).tz(timeZone).format('h:mm A'),
      timeSort: moment(klassDate).tz(timeZone).format('HH:mm'),
      skillLevels: klass.supportedSkillLevels,
      coaches: klass.coaches || [],
      studentCount: klass.studentsCount ?? 0
    }))
  })

  const sortedEvents = allEventsForDay.sort((a, b) => a.timeSort.localeCompare(b.timeSort))

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()} direction="right">
      <DrawerContent className="max-h-[96vh] md:max-h-screen">
        <DrawerHeader className="border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-600" />
            <DrawerTitle className="text-xl text-balance">{formattedDate}</DrawerTitle>
          </div>
          <DrawerDescription className="text-pretty">Open a class to take attendance or preview details</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {sortedEvents.map((event, index) => (
              <div
                key={`${event.klassId}-${index}`}
                className="flex gap-2 items-stretch"
              >
                <button
                  type="button"
                  onClick={() => onEventOpenClass(event.klassId, dayKey, event.timeSort, event.klassDate)}
                  className={cn(
                    'flex-1 min-w-0 p-4 text-left transition-colors bg-white border border-gray-200 rounded-lg',
                    'hover:border-blue-500 hover:bg-blue-50',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 group'
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-semibold text-gray-900 tabular-nums">{event.time}</p>
                      <p className="text-sm text-gray-600">
                        {levelArrayToString(event.skillLevels.map((sl) => sl.name)) || 'All levels'}
                      </p>
                      {event.coaches.length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          {event.coaches.map((c) => c.preferredName || c.fullName?.split(' ')[0]).join(', ')}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        {event.studentCount} student{event.studentCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 transition-colors group-hover:text-blue-500" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEventPreview(
                      event.klassId,
                      dayKey,
                      event.timeSort,
                      event.klassDate
                    )
                  }}
                  className={cn(
                    'shrink-0 flex items-center justify-center px-3 min-h-11 min-w-11 rounded-lg border border-gray-200 bg-white',
                    'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  )}
                  aria-label="Preview class details"
                >
                  <Info className="size-5" aria-hidden />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

DayEventsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  timeZone: PropTypes.string,
  klasses: PropTypes.array,
  onEventOpenClass: PropTypes.func.isRequired,
  onEventPreview: PropTypes.func.isRequired
}

export default DayEventsDrawer
